<script lang="ts">
  /**
   * The three states a screen spends most of its life in: loading, empty, and failed.
   *
   * They were the least considered part of this UI — a grey sentence for "loading", a red sentence
   * for an error, another grey sentence for nothing-here, each written slightly differently in each
   * view. The site has no equivalent to port, so this is built from its tokens rather than copied
   * from it.
   *
   * One component for all three because they occupy the same place on screen and answer the same
   * question — why is there nothing to look at — and because keeping them together is what stops the
   * next screen from inventing a fourth way to say it.
   */
  import type { Snippet } from "svelte";
  import AppButton from "./AppButton.svelte";
  import AppSpinner from "./AppSpinner.svelte";

  let {
    kind,
    title = "",
    retryLabel = "",
    onretry,
    children,
  }: {
    kind: "loading" | "empty" | "error";
    title?: string;
    /** Offered on an error: the thing that failed is usually worth trying again. */
    retryLabel?: string;
    onretry?: () => void;
    children?: Snippet;
  } = $props();
</script>

<div class="flex flex-col items-center justify-center gap-3 py-12 px-4 text-center">
  {#if kind === "loading"}
    <AppSpinner size="md" class="text-ink-muted" />
  {:else if kind === "empty"}
    <!-- An empty shelf and a failed request look different on purpose. Wearing the same grey text,
         as they used to, made "there is nothing here" and "we could not find out" indistinguishable
         — and only one of those is worth retrying. -->
    <svg
      viewBox="0 0 24 24"
      class="w-10 h-10 text-ink-muted"
      fill="none"
      stroke="currentColor"
      stroke-width="1.4"
    >
      <path d="M3 7a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.6.8l1 1.4a2 2 0 0 0 1.6.8H19a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  {:else}
    <svg viewBox="0 0 24 24" class="w-10 h-10 text-error" fill="none" stroke="currentColor" stroke-width="1.4">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5M12 16h.01" />
    </svg>
  {/if}

  <p class="text-sm max-w-sm {kind === 'error' ? 'text-error' : 'text-ink-muted'}">
    {#if children}{@render children()}{:else}{title}{/if}
  </p>

  {#if kind === "error" && retryLabel}
    <AppButton variant="secondary" size="sm" onclick={onretry}>
      {retryLabel}
    </AppButton>
  {/if}
</div>
